<script lang="ts">
  import type { Readable } from "svelte/store";
  import type { VideoBox } from "../../Space/VideoBox";
  import VideoMediaBox from "./VideoMediaBox.svelte";

  interface Props {
    videoBox: VideoBox;
    distanceVisible: Readable<boolean>;
    selfPreview?: boolean;
  }

  let { videoBox, distanceVisible, selfPreview = false }: Props = $props();

  let streamableStore = $derived(videoBox.streamable);
  let streamable = $derived($streamableStore);
  let hasVideoStore = $derived(streamable?.hasVideo);
  let blockedStore = $derived(streamable?.media.isBlocked);
  let statusStore = $derived(videoBox.statusStore);
  let frameReady = $state(false);
  let streamIdentity: string | undefined;

  $effect(() => {
    const nextIdentity = streamable ? `${streamable.uniqueId}:${streamable.media.type}` : undefined;
    if (streamIdentity !== nextIdentity) {
      streamIdentity = nextIdentity;
      frameReady = false;
    }
    if (!$hasVideoStore) {
      frameReady = false;
    }
  });

  // The self preview persists for as long as the local camera streams (same rule
  // as remote overlays); `frameReady` still holds it back until the first frame
  // decodes so it never flashes an empty box. (There was previously a 4s
  // self-only auto-hide here; removed so a streamer keeps seeing their own
  // video over their avatar the whole time.)
  let visible = $derived(
    $distanceVisible &&
      $hasVideoStore === true &&
      $blockedStore !== true &&
      $statusStore === "connected" &&
      frameReady,
  );

  function markVideoReady(): void {
    if (frameReady) return;
    frameReady = true;
  }
</script>

<div
  class="avatar-video-card"
  class:is-visible={visible}
  class:self-preview={selfPreview}
  aria-hidden={!visible}
  data-testid={selfPreview ? "avatar-video-self-preview" : "avatar-video-overlay"}
  data-space-user-id={videoBox.spaceUser.spaceUserId}
  data-visible={visible ? "true" : "false"}
>
  <VideoMediaBox
    {videoBox}
    miniMode={true}
    activeUserName={false}
    registerScreenBlocker={false}
    onvideoready={markVideoReady}
  />
</div>

<style>
  .avatar-video-card {
    width: 84px;
    height: 47px;
    overflow: hidden;
    border: 1px solid rgba(74, 59, 43, 0.95);
    border-radius: 4px;
    background: #141210;
    box-shadow: 0 5px 16px rgba(0, 0, 0, 0.48);
    opacity: 0;
    pointer-events: none !important;
    transform: translateY(3px) scale(0.96);
    transition:
      opacity 120ms ease-out,
      transform 120ms ease-out;
  }

  .avatar-video-card.is-visible {
    opacity: 1;
    transform: translateY(0) scale(1);
  }

  .avatar-video-card.self-preview {
    border-color: rgba(217, 154, 61, 0.9);
  }

  .avatar-video-card :global(*) {
    pointer-events: none !important;
  }
</style>
